/**
 * Grup bloğunun başındaki renkli çip: tıklayınca grubu katlar/açar.
 *
 * Ad düzenleme çift tıkla DEĞİL sağ tık menüsünden: tek tık zaten katlama/açma demek ve
 * ikisi aynı elemanda çakışıyor — çift tıkın ilk tıklaması grubu katlayıp DOM'u
 * değiştirdiği için ad düzenleme çoğu zaman hiç açılmıyordu. Chrome'da da grup adı
 * menüden veriliyor.
 *
 * Adsız grup Chrome'da sadece renkli bir nokta olarak durur — ad zorunlu tutulmuyor, çünkü
 * çoğu grup "şu an baktığım üç ekran" demek ve ad vermek fazladan iş.
 */
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { groupPalette } from '../lib/tabGroupColors';
import type { TabGroup } from '../model/types';
import { InlineRename } from './InlineRename';

export interface TabGroupChipProps {
  group: TabGroup;
  count: number;
  renaming: boolean;
  onToggle: () => void;
  onRenameCommit: (value: string) => void;
  onRenameCancel: () => void;
}

export function TabGroupChip({
  group,
  count,
  renaming,
  onToggle,
  onRenameCommit,
  onRenameCancel,
}: TabGroupChipProps) {
  const { t } = useTranslation();
  const palette = groupPalette(group.color);
  const label = group.name || t('shell.tab.group.untitled');

  return (
    <button
      type="button"
      aria-expanded={!group.collapsed}
      aria-label={`${label} (${count})`}
      title={label}
      onClick={() => !renaming && onToggle()}
      className={cn(
        'flex h-6 shrink-0 items-center gap-1 self-center rounded-md px-2 text-xs font-medium',
        group.name || renaming ? '' : 'w-6 justify-center px-0',
      )}
      style={{ backgroundColor: palette.bg, color: palette.fg }}
    >
      {renaming ? (
        <InlineRename
          value={group.name}
          aria-label={t('shell.tab.group.rename')}
          placeholder={t('shell.tab.group.untitled')}
          onCommit={onRenameCommit}
          onCancel={onRenameCancel}
          className="w-24 min-w-0 rounded border-0 bg-white/25 px-1 py-0 text-xs text-inherit outline-none placeholder:text-inherit/60"
        />
      ) : group.name ? (
        <span className="max-w-[9rem] truncate">{group.name}</span>
      ) : null}
      {/* Katlıyken üye sayısı görünür; açıkken sekmeler zaten ortada. */}
      {group.collapsed ? <span className="tabular-nums opacity-90">{count}</span> : null}
    </button>
  );
}
